import React, { useEffect, useRef, useState } from 'react'
import { Service } from '../../types/Service'
import { fetchAllServicesInCategory, fetchServiceImage } from '../../services/Service'
import ServiceCard from './ServiceCard';
import LoadMore from '../../../utils/LoadMore';
import sendToast from '../../../utils/Toast';
import './style.less';
import { Button, Message } from 'semantic-ui-react';
import { useHistory } from 'react-router-dom';
import useResidentOrderCardEligibility from '../../hooks/useResidentOrderCardEligibility';
import { isPaymentRequiredForService } from '../../utils/paymentStatus';
import { CardGateReason, getCardGateActionLabel, getCardGateMessage, resolveCardGateReason } from '../../utils/orderCardGate';

interface StepTwoProps {
    selectedServiceCategoryName: string;
    selectedResidentId: string;
    setIsFetching: React.Dispatch<React.SetStateAction<boolean>>;
    setStepForm: React.Dispatch<React.SetStateAction<number>>;
    setSelectedServiceId: React.Dispatch<React.SetStateAction<string>>;
    isFetching: boolean;
    FacilityId: string;
    residentId: string;
    facilityPaymentsEnabled?: boolean;
}

const StepTwo: React.FC<StepTwoProps> = ({
    FacilityId,
    selectedServiceCategoryName,
    selectedResidentId, setIsFetching,
    setStepForm,
    setSelectedServiceId,
    isFetching,
    residentId,
    facilityPaymentsEnabled = false,
}) => {
    const history = useHistory();
    const [services, setServices] = useState<Service[]>([]);
    const [page_no, setPage_no] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(false);
    const [serviceImages, setServiceImages] = useState<Record<string, string>>({});
    const [cardGateReason, setCardGateReason] = useState<CardGateReason>(null);
    const {
        loading: cardEligibilityLoading,
        hasDefaultCard,
        hasAnyCard,
        defaultCardExpired,
        lookupFailed,
        refresh,
    } = useResidentOrderCardEligibility({
        residentId,
        facilityId: FacilityId,
        enabled: facilityPaymentsEnabled,
    });
    const lastQueryKeyRef = useRef<string>('');
    const page_size = 5;

    useEffect(() => {
        if (!cardGateReason) return;
        setCardGateReason(resolveCardGateReason({
            loading: cardEligibilityLoading,
            lookupFailed,
            hasDefaultCard,
            hasAnyCard,
            defaultCardExpired,
        }));
    }, [cardGateReason, cardEligibilityLoading, hasDefaultCard, hasAnyCard, defaultCardExpired, lookupFailed]);

    useEffect(() => {
        const fetchServices = async () => {
            const queryKey = `${selectedServiceCategoryName}:${selectedResidentId}`;
            const isNewQuery = lastQueryKeyRef.current !== queryKey;
            if (isNewQuery) {
                lastQueryKeyRef.current = queryKey;
                if (page_no !== 1) {
                    setPage_no(1);
                    setServices([]);
                    setHasMore(false);
                    setServiceImages({});
                    return;
                }
                setServices([]);
                setHasMore(false);
                setServiceImages({});
            }
            setIsFetching(true);
            const facilityId = FacilityId;
            if (!facilityId) {
                throw new Error('Facility ID is not available');
            }
            try {
                const services = await fetchAllServicesInCategory({
                    pageNo: page_no,
                    pageSize: page_size,
                    facilityId,
                    category: selectedServiceCategoryName,
                    showActiveCategories: true,
                    bookableOnly: true,
                    residentId: selectedResidentId
                });
                const allServices: Service[] = services?.Result?.Content?.Result || [];
                const totalRecords = services?.Result?.Content?.TotRecords || 0;
                const hasMoreServices = totalRecords > page_no * page_size;
                setHasMore(hasMoreServices);
                if (allServices.length > 0) {
                    const imagesMap = await fetchServiceImages(allServices, FacilityId);
                    setServiceImages((prevImages) => ({ ...prevImages, ...imagesMap }));
                    // Check if there are more services to load
                    setServices((prevServices) => {
                        const existingIds = new Set(prevServices.map(service => service._id)); // or `id`
                        const newServices = allServices.filter(service => !existingIds.has(service._id));
                        return [...prevServices, ...newServices];
                    });
                }
            }
            catch (error) {
                sendToast('error', error instanceof Error ? error.message : 'An error occurred while fetching services');
            }
            finally {
                setIsFetching(false);
            }
        };
        fetchServices();
    }, [FacilityId, selectedServiceCategoryName, selectedResidentId, page_no, page_size, setIsFetching]);

    const fetchServiceImages = async (services, FacilityId) => {
        const servicesWithImages = services.filter((service) => service.image);
        const imagePromises = servicesWithImages.map(async (service) => {
            if (service.image) {
                const imageUrl = await fetchServiceImage({
                    serviceId: service._id,
                    fileName: service.image,
                    Facility: (FacilityId) || ''
                }
                );
                return { [service._id]: imageUrl };
            }
        });
        const imageResults = await Promise.all(imagePromises);
        return Object.assign({}, ...imageResults);
    };

    const handleShowServiceDetails = (service: Service) => {
        if (!service || !service._id) {
            sendToast('error', 'Service ID is not available');
            return;
        }

        const paymentRequired = facilityPaymentsEnabled && isPaymentRequiredForService(service);
        if (paymentRequired) {
            const reason = resolveCardGateReason({
                loading: cardEligibilityLoading,
                lookupFailed,
                hasDefaultCard,
                hasAnyCard,
                defaultCardExpired,
            });
            if (reason) {
                setCardGateReason(reason);
                return;
            }
        }

        setCardGateReason(null);
        setSelectedServiceId(service._id);
        setStepForm(3);
    }

    const handleRetryCardEligibility = async () => {
        await refresh();
    };

    return (
        <div>
            {
                services.length > 0 ? (
                    <div>
                        {cardGateReason && (
                            <Message warning className="orderCardGateNotice">
                                <Message.Header>Card Required For Payable Order</Message.Header>
                                <p>{getCardGateMessage(cardGateReason)}</p>
                                <div className="orderCardGateActions">
                                    <Button size="small" primary onClick={() => history.push('/payments')}>
                                        {getCardGateActionLabel(cardGateReason)}
                                    </Button>
                                    <Button size="small" onClick={handleRetryCardEligibility} loading={cardEligibilityLoading} disabled={cardEligibilityLoading}>
                                        Retry
                                    </Button>
                                </div>
                            </Message>
                        )}
                        <div className="stepTwoServiceList">
                            {services.map((service) => {
                                const serviceImage = serviceImages[service._id] || '';
                                return (
                                    <div
                                        key={service._id}
                                        className="stepTwoServiceCardWrapper"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => handleShowServiceDetails(service)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleShowServiceDetails(service);
                                            }
                                        }}
                                    >
                                        <ServiceCard
                                            service={service}
                                            serviceImage={serviceImage}
                                            compact
                                            facilityPaymentsEnabled={facilityPaymentsEnabled}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : <div style={{ textAlign: "center", marginTop: "20px" }}>
                    No services available for this category.
                </div>
            }
            <div style={{ marginTop: "10px" }}>
                <LoadMore
                    isLoading={isFetching}
                    hasMore={hasMore}
                    next={() => setPage_no((prev) => prev + 1)}
                    skipMargin={true}
                />
            </div>
        </div>
    )
}

export default StepTwo
